import { useSignal } from '@preact/signals';
import { DeviceTypes } from 'utils/constants';
import { useEffect } from 'preact/hooks';
import { isSSR } from 'utils/isSSR';

/**
 * Accepts a media query string then uses the
 * [window.matchMedia](https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia) API to determine if it
 * matches with the current document.<br />
 * It also monitor the document changes to detect when it matches or stops matching the media query.<br />
 * Returns the validity state of the given media query.
 *
 * Implementation inspired by [beautiful-react-hooks](https://github.com/antonioru/beautiful-react-hooks/blob/master/src/useMediaQuery.ts)
 */
export const useMediaQuery = (mediaQuery: string, serverValue = false) => {
	const returnedSignal = useSignal(serverValue);

	if (isSSR()) return returnedSignal;

	if (!globalThis?.window?.matchMedia) {
		throw new Error('useMediaQuery is a client-side only hook.');
	}

	useEffect(() => {
		const matchMedia = globalThis.window.matchMedia(mediaQuery);
		const onChange = () => {
			returnedSignal.value = matchMedia.matches;
		};

		matchMedia.addEventListener('change', onChange);

		onChange();
		return () => {
			matchMedia.removeEventListener('change', onChange);
		};
	}, [mediaQuery]);

	return returnedSignal;
};

export const useIsDesktopAndLarger = (width = DeviceTypes.desktop.breakpoint, serverValue = false) =>
	useMediaQuery(`(min-width: ${width}px)`, serverValue);

export const useIsTabletAndLarger = (width = DeviceTypes.tablet.breakpoint, serverValue = false) =>
	useMediaQuery(`(min-width: ${width}px)`, serverValue);

export const useIsTablet = (
	minWidth = DeviceTypes.tablet.breakpoint,
	maxWidth = DeviceTypes.desktop.breakpoint,
	serverValue = false,
) => useMediaQuery(`(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`, serverValue);

export const useIsMobileWideAndLarger = (width = DeviceTypes.mobileWide.breakpoint, serverValue = false) =>
	useMediaQuery(`(min-width: ${width}px)`, serverValue);

export const useIsMobileWide = (
	minWidth = DeviceTypes.mobileWide.breakpoint,
	maxWidth = DeviceTypes.tablet.breakpoint,
	serverValue = false,
) => useMediaQuery(`(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`, serverValue);

export const useIsMobile = (width = DeviceTypes.mobileWide.breakpoint, serverValue = false) =>
	useMediaQuery(`(max-width: ${width}px)`, serverValue);
